import { CommonModule, HashLocationStrategy, LocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, Injector, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BreakpointService } from '@optek/xm-bootstrap';
import {
  MsalModule,
  MsalService,
  MsalGuard,
  MsalInterceptor,
  MsalRedirectComponent,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';
import {
  msalInstanceFactory,
  msalGuardConfigFactory,
  msalInterceptorConfigFactory,
} from './msal-config';
import { WINDOW_PROVIDERS } from '@services/core/window.service';
import { ErrorHandlerService } from '@services/error-handler.service';
import { FullScreenService } from '@services/full-screen.service';
import { RefactorService } from '@services/refactor.service';
import { TeamService } from '@services/team.service';
import { UserService } from '@services/user.service';
import { LiveMeterService } from '@services/xhr/live-meter-svc';
import { MeterDataService } from '@services/xhr/meter-data-svc';
import { MsoHierarchyService } from '@services/xhr/mso-hierarchy-svc';
import { WebAppService } from '@services/xhr/web-app-svc';
import { MapModule } from '@shared/map/map.module';
import { NotificationToastDefaults } from '@shared/notification/toastDefaults';
import { switchMap } from 'rxjs/operators';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FullModule } from './full/full.module';
import { BreadcrumbService } from './full/header/breadcrumb/breadcrumb.service';
import { AuthInterceptorService } from '@services/auth-interceptor.service';
import { PipesModule } from './pipes/pipes.module';


@NgModule({
  imports: [
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    CommonModule,
    FormsModule,
    FullModule,
    MsalModule,
    PipesModule,
    ReactiveFormsModule,
    MapModule
  ],
  declarations: [
    AppComponent,
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    BreakpointService,
    ErrorHandlerService,
    FullScreenService,
    MeterDataService,
    LiveMeterService,
    MsoHierarchyService,
    RefactorService,
    TeamService,
    BreadcrumbService,
    UserService,
    WebAppService,
    WINDOW_PROVIDERS,
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy
    },
    {
      provide: APP_INITIALIZER,
      useFactory: loadUserProfile,
      multi: true,
      deps: [UserService, Injector]
    },
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: msalInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: msalGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: msalInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    {
      provide: 'NotificationToastConfig', useValue: NotificationToastDefaults,
    }],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule { }

export function loadUserProfile(
  userService: UserService,
  injector: Injector,
): () => Promise<any> {
  return (): Promise<any> => {
    return new Promise((resolve, reject) => {
      userService.getUserProfile()
        .pipe(
          switchMap(data => {
            return userService.fetchProfilePreferences();
          }
          )
        ).subscribe(
          prefs => {
            userService.userServiceStatus$.next('loaded');
            // @ts-ignore
            resolve();
          }, err => {
            console.error(err);
            reject();
          }
        );
      // @ts-ignore
      resolve();
    });
  };
}
